/* ============================================
   QUOTE WORKSTATION — Row Reorder
   Asset: workstation-reorder.js
   Drag-to-reorder line items within a vendor group. Series items move
   as a contiguous block. Cross-vendor drops are blocked.
   Also reorders the sub-text lines inside a single item (see the
   sub-text block near the bottom).
   ============================================ */
(function() {
  'use strict';
  var Q = window.QWS;
  if (!Q) return;
  var state = Q.state;
  var wrap = Q.wrap;

  // Row is draggable=false by default so inputs/buttons inside the row stay
  // clickable. We arm the row only when the user grabs it by the handle, then
  // disarm on dragend or stray mouseup (drag never started).
  var armedRow = null;
  var dragData = null; // { itemId, vendor, seriesId }

  wrap.addEventListener('mousedown', function(e) {
    var handle = e.target.closest('.rq-ws-table__drag-handle');
    if (!handle) return;
    var row = handle.closest('tr[data-item-id]');
    if (!row) return;
    row.setAttribute('draggable', 'true');
    armedRow = row;
  });

  wrap.addEventListener('mouseup', function() {
    if (armedRow && !dragData) {
      armedRow.removeAttribute('draggable');
      armedRow = null;
    }
  });

  wrap.addEventListener('dragstart', function(e) {
    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-21) — A sub-text line is
       dragging, not a row. Sub-text rows sit inside the item row's <td>, so
       without this bail the lookup below finds the parent <tr>, sees it
       unarmed, and preventDefaults the sub-text drag before it can start.
       This listener is registered first, so the guard has to live here. */
    if (e.target.closest && e.target.closest('.rq-ws-subdesc-area')) return;
    /* END CUSTOM_RFQPORTAL_BY_CLAUDE */
    var row = e.target.closest && e.target.closest('tr[data-item-id]');
    if (!row || row !== armedRow) { e.preventDefault && e.preventDefault(); return; }
    var itemId = row.getAttribute('data-item-id');
    var item = findItem(itemId);
    if (!item) { e.preventDefault(); return; }
    dragData = {
      itemId: itemId,
      vendor: row.getAttribute('data-row-vendor') || item.vendor || '',
      seriesId: item.seriesId || null,
      /* CUSTOM_RFQPORTAL_BY_CLAUDE — Record pointer origin so the drop
         handler can distinguish a horizontal "indent" gesture from a
         vertical "reorder" gesture. Browsers fire dragstart after a small
         threshold movement (≈5px), so these coords are already a few
         pixels into the drag — fine for delta math. */
      startX: e.clientX,
      startY: e.clientY,
      /* END CUSTOM_RFQPORTAL_BY_CLAUDE */
    };
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = 'move';
      try { e.dataTransfer.setData('text/plain', itemId); } catch (_) {}
    }
    if (dragData.seriesId) {
      var members = wrap.querySelectorAll('tr[data-series-id="' + cssEscape(dragData.seriesId) + '"]');
      members.forEach(function(m) { m.classList.add('rq-ws-row--dragging'); });
    } else {
      row.classList.add('rq-ws-row--dragging');
    }
  });

  wrap.addEventListener('dragover', function(e) {
    if (!dragData) return;
    var row = e.target.closest && e.target.closest('tr[data-item-id]');
    if (!row) return;
    var rowVendor = row.getAttribute('data-row-vendor') || '';
    if (rowVendor !== dragData.vendor) return;
    e.preventDefault();
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
    /* CUSTOM_RFQPORTAL_BY_CLAUDE — Gesture-aware drop indicator. Same
       horizontal-vs-vertical decision the drop handler uses, surfaced
       live during dragover so the rep sees WHICH action will fire:
         • Horizontal-dominant on the source row (|dx|>|dy|, |dx|>25)
           → show indent-preview (right-arrow / left-arrow on the
             source row, no above/below blue lines).
         • Otherwise → normal reorder above/below blue lines. */
    /* CUSTOM_RFQPORTAL_BY_CLAUDE — Stash the latest cursor position on
       dragData so dragend (which fires regardless of where the user
       released) can recover the gesture. Without this, dragging LEFT
       off the row's left edge meant drop never fired and the unindent
       never executed. */
    dragData.lastClientX = e.clientX;
    dragData.lastClientY = e.clientY;
    /* END CUSTOM_RFQPORTAL_BY_CLAUDE */
    var dx = e.clientX - (dragData.startX || e.clientX);
    var dy = e.clientY - (dragData.startY || e.clientY);
    var targetId = row.getAttribute('data-item-id');
    var sameRow = targetId === dragData.itemId;
    var indentMode = sameRow && Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 25;
    if (indentMode) {
      clearIndicatorClasses();
      showIndentPreview(dragData.itemId, dx > 0 ? 'right' : 'left');
      return;
    }
    clearIndentPreview();
    var rect = row.getBoundingClientRect();
    var pos = e.clientY < rect.top + rect.height / 2 ? 'above' : 'below';
    showIndicator(row, pos);
    /* END CUSTOM_RFQPORTAL_BY_CLAUDE */
  });

  wrap.addEventListener('drop', function(e) {
    if (!dragData) return;
    var row = e.target.closest && e.target.closest('tr[data-item-id]');
    if (!row) return;
    var rowVendor = row.getAttribute('data-row-vendor') || '';
    if (rowVendor !== dragData.vendor) return;
    e.preventDefault();

    /* CUSTOM_RFQPORTAL_BY_CLAUDE — Indent gesture detection.
       Compute pointer delta from dragstart. If the drag was predominantly
       horizontal AND landed on the SAME row it started from, treat it as
       an indent toggle instead of a reorder:
         dx > +25 → indent (mark as child of preceding item)
         dx < -25 → unindent (back to top-level)
       Reorder requires a vertical-dominant gesture. Series-item rows can
       still be indented but the indent applies only to the lead row
       (series children always inherit the lead's indent visually).
       Drop on a DIFFERENT row always reorders, regardless of horizontal
       drift, since user clearly meant to drag across rows. */
    var dx = e.clientX - (dragData.startX || e.clientX);
    var dy = e.clientY - (dragData.startY || e.clientY);
    var targetId = row.getAttribute('data-item-id');
    var sameRow = targetId === dragData.itemId;
    if (sameRow && Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 25) {
      var dragged = findItem(dragData.itemId);
      if (dragged) {
        var nextIndented = dx > 0;
        if (!!dragged.indented !== nextIndented) {
          dragged.indented = nextIndented;
          state.dirty = true;
          /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-28) — preserve scrollY
             across the renderItems rebuild so the rep doesn't get yanked
             to the top when they indent/outdent a row deep in the quote.
             Same pattern the +5 / remove-X handlers use in workstation-
             table.js: blur active element + snapshot scrollY + restore. */
          var _scrollY = window.scrollY || document.documentElement.scrollTop || 0;
          if (document.activeElement && typeof document.activeElement.blur === 'function') {
            document.activeElement.blur();
          }
          Q.renderItems();
          window.scrollTo(0, _scrollY);
          /* END CUSTOM_RFQPORTAL_BY_CLAUDE */
        }
      }
      return;
    }
    /* END CUSTOM_RFQPORTAL_BY_CLAUDE */

    var rect = row.getBoundingClientRect();
    var pos = e.clientY < rect.top + rect.height / 2 ? 'above' : 'below';
    moveItem(dragData.itemId, targetId, pos);
  });

  wrap.addEventListener('dragend', function() {
    /* CUSTOM_RFQPORTAL_BY_CLAUDE — Indent gesture fallback. Native DnD's
       `drop` event only fires when the cursor releases over a valid drop
       target (a `tr[data-item-id]` of the matching vendor). Dragging
       LEFT off the row's left edge typically leaves the table entirely,
       so drop never fires and the indent toggle from the drop handler
       runs zero times. dragend always fires regardless of where the
       release happened. Replay the horizontal-vs-vertical decision here
       using lastClientX/Y (snapshotted in dragover). When drop already
       fired and toggled the same direction, the !== guard inside the
       block makes this a no-op. */
    if (dragData && dragData.lastClientX != null && dragData.lastClientY != null) {
      var dxE = dragData.lastClientX - (dragData.startX || dragData.lastClientX);
      var dyE = dragData.lastClientY - (dragData.startY || dragData.lastClientY);
      if (Math.abs(dxE) > Math.abs(dyE) && Math.abs(dxE) > 25) {
        var dragged = findItem(dragData.itemId);
        if (dragged) {
          var nextIndented = dxE > 0;
          if (!!dragged.indented !== nextIndented) {
            dragged.indented = nextIndented;
            state.dirty = true;
            /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-28) — preserve scrollY
               across the renderItems rebuild (same fix as the drop-handler
               branch above; this is the off-row dragend fallback). */
            var _scrollYE = window.scrollY || document.documentElement.scrollTop || 0;
            if (document.activeElement && typeof document.activeElement.blur === 'function') {
              document.activeElement.blur();
            }
            Q.renderItems();
            window.scrollTo(0, _scrollYE);
            /* END CUSTOM_RFQPORTAL_BY_CLAUDE */
          }
        }
      }
    }
    /* END CUSTOM_RFQPORTAL_BY_CLAUDE */
    if (armedRow) {
      armedRow.removeAttribute('draggable');
      armedRow = null;
    }
    dragData = null;
    clearAllIndicators();
  });

  /* ══════════════════════════════════════════════════════════
     CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-21) — Sub-text line reorder.
     Same grab-by-the-handle contract as the item rows above, scoped to ONE
     item: a sub-text line can only move within its own product, never onto
     a different one (the vendor-colour collapse rule in workstation-items.js
     decides which item a shared line belongs under, and a rep dragging a
     line across products would silently fight it).

     Every handler bails unless a sub row was armed, so the two drags never
     cross: during a sub drag `dragData` is null and the row handlers above
     return early; during a row drag `subDrag` is null and these do.
     ══════════════════════════════════════════════════════════ */
  var armedSub = null;
  var subDrag = null; // { itemId, fromIdx }

  wrap.addEventListener('mousedown', function(e) {
    var h = e.target.closest && e.target.closest('.rq-ws-subdesc-drag');
    if (!h) return;
    var row = h.closest('.rq-ws-subdesc-row');
    if (!row) return;
    row.setAttribute('draggable', 'true');
    armedSub = row;
  });

  wrap.addEventListener('mouseup', function() {
    if (armedSub && !subDrag) {
      armedSub.removeAttribute('draggable');
      armedSub = null;
    }
  });

  wrap.addEventListener('dragstart', function(e) {
    var row = e.target.closest && e.target.closest('.rq-ws-subdesc-row');
    if (!row || row !== armedSub) return;
    var fromIdx = parseInt(row.getAttribute('data-sub-idx'), 10);
    if (isNaN(fromIdx)) return;
    subDrag = { itemId: row.getAttribute('data-item-id'), fromIdx: fromIdx };
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = 'move';
      try { e.dataTransfer.setData('text/plain', 'subdesc:' + fromIdx); } catch (_) {}
    }
    row.classList.add('rq-ws-subdesc-row--dragging');
  });

  wrap.addEventListener('dragover', function(e) {
    if (!subDrag) return;
    var row = subTargetRow(e);
    if (!row) return;
    e.preventDefault();
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
    var rect = row.getBoundingClientRect();
    var pos = e.clientY < rect.top + rect.height / 2 ? 'above' : 'below';
    clearSubIndicators();
    row.classList.add(pos === 'above'
      ? 'rq-ws-subdesc-row--drop-above'
      : 'rq-ws-subdesc-row--drop-below');
  });

  wrap.addEventListener('drop', function(e) {
    if (!subDrag) return;
    var row = subTargetRow(e);
    if (!row) return;
    e.preventDefault();
    var rect = row.getBoundingClientRect();
    var pos = e.clientY < rect.top + rect.height / 2 ? 'above' : 'below';
    moveSubDesc(subDrag.itemId, subDrag.fromIdx, parseInt(row.getAttribute('data-sub-idx'), 10), pos);
  });

  wrap.addEventListener('dragend', function() {
    if (armedSub) {
      armedSub.removeAttribute('draggable');
      armedSub = null;
    }
    subDrag = null;
    clearSubIndicators();
    wrap.querySelectorAll('.rq-ws-subdesc-row--dragging').forEach(function(r) {
      r.classList.remove('rq-ws-subdesc-row--dragging');
    });
  });

  /* A drop target only counts when it belongs to the same item. */
  function subTargetRow(e) {
    var row = e.target.closest && e.target.closest('.rq-ws-subdesc-row');
    if (!row) return null;
    if (row.getAttribute('data-item-id') !== subDrag.itemId) return null;
    return row;
  }

  function clearSubIndicators() {
    wrap.querySelectorAll('.rq-ws-subdesc-row--drop-above, .rq-ws-subdesc-row--drop-below')
      .forEach(function(r) {
        r.classList.remove('rq-ws-subdesc-row--drop-above', 'rq-ws-subdesc-row--drop-below');
      });
  }

  function moveSubDesc(itemId, fromIdx, toIdx, position) {
    var item = findItem(itemId);
    if (!item || !item.subDescriptions) return;
    var arr = item.subDescriptions;
    if (isNaN(fromIdx) || isNaN(toIdx)) return;
    if (fromIdx < 0 || fromIdx >= arr.length || toIdx < 0 || toIdx >= arr.length) return;
    var insert = position === 'above' ? toIdx : toIdx + 1;
    if (insert === fromIdx || insert === fromIdx + 1) return; // lands where it started
    var moved = arr.splice(fromIdx, 1)[0];
    if (fromIdx < insert) insert--;
    arr.splice(insert, 0, moved);
    state.dirty = true;
    /* Same scroll-preserving rebuild the indent gesture uses — renderItems()
       rebuilds every row, and without this the rep gets yanked to the top of
       a long quote. Totals don't move: sub-text carries no price. */
    var _scrollY = window.scrollY || document.documentElement.scrollTop || 0;
    if (document.activeElement && typeof document.activeElement.blur === 'function') {
      document.activeElement.blur();
    }
    Q.renderItems();
    window.scrollTo(0, _scrollY);
  }
  /* END CUSTOM_RFQPORTAL_BY_CLAUDE */

  function findItem(id) {
    for (var i = 0; i < state.items.length; i++) if (state.items[i].id === id) return state.items[i];
    return null;
  }

  function moveItem(draggedId, targetId, position) {
    var dragged = findItem(draggedId);
    var target = findItem(targetId);
    if (!dragged || !target || dragged.id === target.id) return;

    var movingIds = {};
    if (dragged.seriesId) {
      state.items.forEach(function(i) { if (i.seriesId === dragged.seriesId) movingIds[i.id] = true; });
      if (movingIds[targetId]) return; // dropping a series on itself
    } else {
      movingIds[draggedId] = true;
    }

    // Remove moving items, preserving their relative order
    var moving = [];
    state.items = state.items.filter(function(i) {
      if (movingIds[i.id]) { moving.push(i); return false; }
      return true;
    });

    var targetIdx = -1;
    for (var i = 0; i < state.items.length; i++) if (state.items[i].id === targetId) { targetIdx = i; break; }
    if (targetIdx === -1) {
      state.items = state.items.concat(moving);
      finishMove();
      return;
    }

    var insertIdx = position === 'above' ? targetIdx : targetIdx + 1;

    // If dropping above/below a series member, expand the insertion point past
    // the entire series block so the moving item lands outside, not interleaved.
    if (target.seriesId) {
      if (position === 'below') {
        while (insertIdx < state.items.length && state.items[insertIdx].seriesId === target.seriesId) insertIdx++;
      } else {
        while (insertIdx > 0 && state.items[insertIdx - 1].seriesId === target.seriesId) insertIdx--;
      }
    }

    state.items.splice.apply(state.items, [insertIdx, 0].concat(moving));
    finishMove();
  }

  function finishMove() {
    state.dirty = true;
    Q.renderItems();
    Q.recalcTotals();
  }

  function showIndicator(row, position) {
    clearIndicatorClasses();
    var seriesId = row.getAttribute('data-series-id');
    if (seriesId) {
      var members = wrap.querySelectorAll('tr[data-series-id="' + cssEscape(seriesId) + '"]');
      if (!members.length) return;
      if (position === 'above') members[0].classList.add('rq-ws-row--drop-above');
      else members[members.length - 1].classList.add('rq-ws-row--drop-below');
    } else {
      row.classList.add(position === 'above' ? 'rq-ws-row--drop-above' : 'rq-ws-row--drop-below');
    }
  }

  function clearIndicatorClasses() {
    wrap.querySelectorAll('.rq-ws-row--drop-above, .rq-ws-row--drop-below').forEach(function(r) {
      r.classList.remove('rq-ws-row--drop-above', 'rq-ws-row--drop-below');
    });
  }

  /* CUSTOM_RFQPORTAL_BY_CLAUDE — Indent-preview indicator. Applied to the
     source row only (the row being dragged). Distinct from the vertical
     drop-above/drop-below classes so the two preview styles never overlap.
     CSS sits in sections/rep-quote-workstation.liquid. */
  function showIndentPreview(itemId, direction) {
    clearIndentPreview();
    var row = wrap.querySelector('tr[data-item-id="' + cssEscape(itemId) + '"]');
    if (!row) return;
    row.classList.add(direction === 'right'
      ? 'rq-ws-row--indent-preview-right'
      : 'rq-ws-row--indent-preview-left');
  }
  function clearIndentPreview() {
    wrap.querySelectorAll('.rq-ws-row--indent-preview-right, .rq-ws-row--indent-preview-left').forEach(function (r) {
      r.classList.remove('rq-ws-row--indent-preview-right', 'rq-ws-row--indent-preview-left');
    });
  }
  /* END CUSTOM_RFQPORTAL_BY_CLAUDE */

  function clearAllIndicators() {
    clearIndicatorClasses();
    clearIndentPreview();
    wrap.querySelectorAll('.rq-ws-row--dragging').forEach(function(r) {
      r.classList.remove('rq-ws-row--dragging');
    });
  }

  function cssEscape(s) {
    if (window.CSS && CSS.escape) return CSS.escape(s);
    return String(s).replace(/[^a-zA-Z0-9_-]/g, '\\$&');
  }
})();
